// QuestionHeaderText.js
import React, { useState, useRef, useEffect } from "react";
import { Question, QuestionFactory, Serializer } from "survey-core";
import { ReactQuestionFactory } from "survey-react-ui";

const QUESTION_TYPE = "headertext";
let isRegistered = false;

/* =========================================================
   QUESTION MODEL
========================================================= */
class QuestionHeaderTextModel extends Question {
  constructor(name) {
    super(name);

    // Set default values
    this.setPropertyValue("text", "<h3>Header Text</h3>");
    this.setPropertyValue("alignment", "left");
    this.setPropertyValue("fontSize", "24px");
    this.setPropertyValue("fontWeight", "bold");
    this.setPropertyValue("color", "#2c3e50");
    this.setPropertyValue("backgroundColor", "transparent");
    this.setPropertyValue("padding", "10px 0");
    this.setPropertyValue("margin", "0 0 20px 0");
  }

  getType() {
    return QUESTION_TYPE;
  }

  // Text content (HTML supported)
  get text() {
    return this.getPropertyValue("text", "<h3>Header Text</h3>");
  }

  set text(val) {
    this.setPropertyValue("text", val);
  }

  // Alignment
  get alignment() {
    return this.getPropertyValue("alignment", "left");
  }

  set alignment(val) {
    this.setPropertyValue("alignment", val);
  }

  // Font size
  get fontSize() {
    return this.getPropertyValue("fontSize", "24px");
  }

  set fontSize(val) {
    this.setPropertyValue("fontSize", val);
  }

  // Font weight
  get fontWeight() {
    return this.getPropertyValue("fontWeight", "bold");
  }

  set fontWeight(val) {
    this.setPropertyValue("fontWeight", val);
  }

  // Color
  get color() {
    return this.getPropertyValue("color", "#2c3e50");
  }

  set color(val) {
    this.setPropertyValue("color", val);
  }

  // Background color
  get backgroundColor() {
    return this.getPropertyValue("backgroundColor", "transparent");
  }

  set backgroundColor(val) {
    this.setPropertyValue("backgroundColor", val);
  }

  // Get CSS style object
  get headerStyle() {
    return {
      textAlign: this.alignment,
      fontSize: this.fontSize,
      fontWeight: this.fontWeight,
      color: this.color,
      backgroundColor: this.backgroundColor,
      padding: this.padding,
      margin: this.margin,
      border: "none",
      outline: "none",
      width: "100%",
      lineHeight: "1.4",
    };
  }
}

/* =========================================================
   REACT RENDERER with Rich Text Editor
========================================================= */
const HeaderTextComponent = (props) => {
  const question = props.question;
  const [isEditing, setIsEditing] = useState(false);
  const [localText, setLocalText] = useState(
    question.text || "<h3>Header Text</h3>",
  );
  const editorRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    setLocalText(question.text);
  }, [question.text]);

  useEffect(() => {
    if (isEditing && editorRef.current) {
      editorRef.current.innerHTML = question.text || "<h3>Header Text</h3>";
      editorRef.current.focus();
    }
  }, [isEditing]);

  if (!question) {
    return <div>Loading header...</div>;
  }

  // Handle text change
  const handleTextChange = (newText) => {
    setLocalText(newText);
    question.text = newText;
  };

  const toggleEdit = () => {
    if (question.isReadOnly) return;
    setIsEditing((prev) => !prev);
  };

  // Formatting functions
  const applyFormat = (command, value = null) => {
    if (!editorRef.current) return;

    document.execCommand(command, false, value);
    editorRef.current.focus();

    // Update question with formatted HTML
    const newHtml = editorRef.current.innerHTML;
    handleTextChange(newHtml);
  };

  // Render editing mode
  const renderEditor = () => {
    return (
      <div
        style={{
          width: "100%",
          border: "1px solid #3661EB",
          borderRadius: "4px",
          backgroundColor: "white",
        }}
      >
        {/* Formatting toolbar */}
        <div
          style={{
            padding: "8px",
            backgroundColor: "#f8f9fa",
            borderBottom: "1px solid #dee2e6",
            display: "flex",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          {/* Bold */}
          <button
            type="button"
            onClick={() => applyFormat("bold")}
            style={{
              padding: "6px 10px",
              backgroundColor: "#fff",
              border: "1px solid #ced4da",
              borderRadius: "3px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
            title="Bold"
          >
            <strong>B</strong>
          </button>

          {/* Italic */}
          <button
            type="button"
            onClick={() => applyFormat("italic")}
            style={{
              padding: "6px 10px",
              backgroundColor: "#fff",
              border: "1px solid #ced4da",
              borderRadius: "3px",
              cursor: "pointer",
              fontStyle: "italic",
            }}
            title="Italic"
          >
            <em>I</em>
          </button>

          {/* Underline */}
          <button
            type="button"
            onClick={() => applyFormat("underline")}
            style={{
              padding: "6px 10px",
              backgroundColor: "#fff",
              border: "1px solid #ced4da",
              borderRadius: "3px",
              cursor: "pointer",
              textDecoration: "underline",
            }}
            title="Underline"
          >
            U
          </button>

          {/* Font size */}
          <select
            onChange={(e) => applyFormat("fontSize", e.target.value)}
            style={{
              padding: "6px",
              border: "1px solid #ced4da",
              borderRadius: "3px",
              backgroundColor: "#fff",
            }}
            title="Font Size"
          >
            <option value="1">Small</option>
            <option value="3">Normal</option>
            <option value="5">Large</option>
            <option value="7">Huge</option>
          </select>

          {/* Text color */}
          <input
            type="color"
            onChange={(e) => applyFormat("foreColor", e.target.value)}
            style={{
              width: "40px",
              height: "34px",
              padding: "3px",
              border: "1px solid #ced4da",
              borderRadius: "3px",
              cursor: "pointer",
            }}
            title="Text Color"
          />

          {/* Alignment */}
          <button
            type="button"
            onClick={() => applyFormat("justifyLeft")}
            style={{
              padding: "6px 10px",
              backgroundColor: "#fff",
              border: "1px solid #ced4da",
              borderRadius: "3px",
              cursor: "pointer",
            }}
            title="Align Left"
          >
            ⬅
          </button>
          <button
            type="button"
            onClick={() => applyFormat("justifyCenter")}
            style={{
              padding: "6px 10px",
              backgroundColor: "#fff",
              border: "1px solid #ced4da",
              borderRadius: "3px",
              cursor: "pointer",
            }}
            title="Align Center"
          >
            ↔
          </button>
          <button
            type="button"
            onClick={() => applyFormat("justifyRight")}
            style={{
              padding: "6px 10px",
              backgroundColor: "#fff",
              border: "1px solid #ced4da",
              borderRadius: "3px",
              cursor: "pointer",
            }}
            title="Align Right"
          >
            ➡
          </button>

          {/* Done button */}
          <button
            type="button"
            onClick={toggleEdit}
            style={{
              marginLeft: "auto",
              padding: "6px 12px",
              backgroundColor: "#3661EB",
              color: "white",
              border: "none",
              borderRadius: "3px",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            Done
          </button>
        </div>

        {/* Editable content area */}
        <div
          ref={editorRef}
          contentEditable={!question.isReadOnly}
          onInput={(e) => {
            const html = e.currentTarget.innerHTML;
            question.text = html; // sync only to SurveyJS model
          }}
          style={{
            ...question.headerStyle,
            minHeight: "60px",
            padding: "12px",
            outline: "none",
            fontFamily: "inherit",
          }}
        />
      </div>
    );
  };

  // Render view mode
  const renderView = () => {
    return (
      <div
        ref={contentRef}
        onClick={toggleEdit}
        style={{
          ...question.headerStyle,
          cursor: question.isReadOnly ? "default" : "pointer",
          transition: "all 0.2s",
          border: "1px solid transparent",
          borderRadius: "4px",
          position: "relative",
        }}
        className={!question.isReadOnly ? "header-text-editable" : ""}
      >
        {/* Show HTML content */}
        <div dangerouslySetInnerHTML={{ __html: localText }} />

        {/* Edit hint (only when not read-only) */}
        {!question.isReadOnly && !isEditing && (
          <div
            style={{
              position: "absolute",
              top: "5px",
              right: "5px",
              fontSize: "12px",
              color: "#6c757d",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              padding: "2px 6px",
              borderRadius: "3px",
              opacity: 0,
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => (e.target.style.opacity = 1)}
            onMouseLeave={(e) => (e.target.style.opacity = 0)}
          >
            Click to edit
          </div>
        )}
      </div>
    );
  };

  return isEditing ? renderEditor() : renderView();
};

/* =========================================================
   REGISTRATION
========================================================= */
export function registerHeaderTextQuestion() {
  if (isRegistered) return;
  isRegistered = true;

  Serializer.addClass(
    QUESTION_TYPE,
    [
      {
        name: "text",
        type: "html",
        category: "content",
        displayName: "Header Content",
        default: "<h3>Header Text</h3>",
        visible: true,
      },
      {
        name: "alignment",
        type: "text",
        category: "style",
        displayName: "Text Alignment",
        default: "left",
        choices: ["left", "center", "right", "justify"],
        visible: true,
      },
      {
        name: "fontSize",
        type: "text",
        category: "style",
        displayName: "Font Size",
        default: "24px",
        choices: ["16px", "18px", "20px", "24px", "28px", "32px", "36px"],
        visible: true,
      },
      {
        name: "fontWeight",
        type: "text",
        category: "style",
        displayName: "Font Weight",
        default: "bold",
        choices: ["normal", "bold", "300", "400", "500", "600", "700"],
        visible: true,
      },
      {
        name: "color",
        type: "color",
        category: "style",
        displayName: "Text Color",
        default: "#2c3e50",
        visible: true,
      },
      {
        name: "backgroundColor",
        type: "color",
        category: "style",
        displayName: "Background Color",
        default: "transparent",
        visible: true,
      },
      {
        name: "padding",
        type: "text",
        category: "spacing",
        displayName: "Padding",
        default: "10px 0",
        visible: true,
      },
      {
        name: "margin",
        type: "text",
        category: "spacing",
        displayName: "Margin",
        default: "0 0 20px 0",
        visible: true,
      },
    ],
    () => new QuestionHeaderTextModel(""),
    "question",
  );

  QuestionFactory.Instance.registerQuestion(
    QUESTION_TYPE,
    (name) => new QuestionHeaderTextModel(name),
  );

  ReactQuestionFactory.Instance.registerQuestion(QUESTION_TYPE, (props) => (
    <HeaderTextComponent {...props} />
  ));
}

// Auto-register
if (typeof window !== "undefined") {
  registerHeaderTextQuestion();
}

export default QuestionHeaderTextModel;
